import { observer } from 'mobx-react-lite'
import { createLayeredContext } from '@/contexts/layered-context'
import { type ReactNode, useMemo } from 'react'
import './ChatSupportSpaceManager.module.css'
import { usePageBottomOffset } from '@/contexts/page-bottom-offset-context'
import { normalizeCssNumber } from '@/utils/browser-util'
import { useObservable, useSubject } from '@/hooks/rx-hooks'
import { useFeatures } from '@/contexts/root-store-context'
import {
  animationFrameScheduler,
  BehaviorSubject,
  combineLatestWith,
  fromEventPattern,
  mergeWith,
  Observable,
  switchMap,
} from 'rxjs'
import { distinctUntilChanged, map, observeOn, tap } from 'rxjs/operators'
import { shareLatest } from '@/utils/rx-util'
import { useIsShowingModal } from '@/components/ModalDialog/ModalStackContext'
import { isSpacious$ } from '@/hooks/viewport-hooks'

export interface ChatSupportSpaceManagerProps {
  children: ReactNode
}

/**
 * Provides child components the ability to interact with the support chat.
 */
export const ChatSupportWidgetManagerProvider = observer(
  function ChatSupportSpaceManagerProvider({
    children,
  }: ChatSupportSpaceManagerProps) {
    return (
      <ChatWidgetPositionContextProvider>
        <ChatWidgetHiddenContextProvider>
          <ChatSupportSpaceManager>{children}</ChatSupportSpaceManager>
        </ChatWidgetHiddenContextProvider>
      </ChatWidgetPositionContextProvider>
    )
  },
)

/**
 * Handles updating the support widget's positioning and events.
 */
const ChatSupportSpaceManager = observer(function ChatSupportSpaceManager({
  children,
}: ChatSupportSpaceManagerProps) {
  const offset = usePageBottomOffset()
  const widgetOffset = useChatWidgetPositionValue() ?? offset
  const isShowingModal = useIsShowingModal()
  const isHidden = (useChatWidgetHiddenValue() ?? false) || isShowingModal
  const widgetBottomOffset = useMemo(
    () => normalizeCssNumber(widgetOffset),
    [widgetOffset],
  )

  const {
    support: { chatSupportStore },
  } = useFeatures(({ support }) => ({ support }))

  const isHidden$ = useSubject(() => new BehaviorSubject(isHidden))
  const bottomOffset$ = useSubject(
    () => new BehaviorSubject(widgetBottomOffset),
  )

  if (isHidden$.value !== isHidden) {
    isHidden$.next(isHidden)
  }

  if (bottomOffset$.value !== widgetBottomOffset) {
    bottomOffset$.next(widgetBottomOffset)
  }

  useObservable(
    useMemo(
      () =>
        hubSpotManagementStream(
          isHidden$,
          bottomOffset$,
          isSpacious$,
          chatSupportStore.open$,
        ),
      [isHidden$, bottomOffset$, chatSupportStore.open$],
    ),
  )

  return <>{children}</>
})

const {
  Provider: ChatWidgetPositionContextProvider,
  useProvide: useProvideChatWidgetPositionValue,
  useValue: useChatWidgetPositionValue,
} = createLayeredContext<string | number | null>(null, { excludeNulls: true })

/**
 * Provides a bottom offset for the embedded support chat,
 * essentially moving it upward by the specified amount.
 *
 * @param value
 * @param priority
 */
export function useProvideSupportWidgetBottomOffset(
  value: string | number | null,
  priority = 0,
) {
  useProvideChatWidgetPositionValue(value, priority)
}

const {
  Provider: ChatWidgetHiddenContextProvider,
  useProvide: useProvideChatWidgetHiddenValue,
  useValue: useChatWidgetHiddenValue,
} = createLayeredContext<boolean | null>(false, { excludeNulls: true })

/**
 * Provides a bottom offset for the embedded support chat,
 * essentially moving it upward by the specified amount.
 *
 * @param isHidden
 */
export function useProvideSupportWidgetHidden(isHidden: boolean | null) {
  useProvideChatWidgetHiddenValue(isHidden, isHidden ? 1 : 0)
}

/**
 * Manages the HubSpot widget's positioning and visibility.
 */
function hubSpotManagementStream(
  isHidden$: Observable<boolean>,
  bottomOffset$: Observable<number | string | null>,
  isSpacious$: Observable<boolean>,
  requestOpen$: Observable<void>,
) {
  // Emits the HubSpot widget whenever it's ready.
  const widget$ = new Observable<
    Exclude<typeof window.HubSpotConversations, undefined>
  >((observer) => {
    if (window.HubSpotConversations) {
      observer.next(window.HubSpotConversations)
      observer.complete()
      return
    }

    window.hsConversationsOnReady ??= []
    window.hsConversationsOnReady.push(() => {
      if (window.HubSpotConversations) {
        observer.next(window.HubSpotConversations)
        observer.complete()
      }
    })
  }).pipe(
    switchMap((widget) =>
      fromEventPattern(
        (h) => widget.on('widgetLoaded', h),
        (h) => widget.off('widgetLoaded', h),
      ).pipe(map(() => widget)),
    ),
    shareLatest(),
  )

  // Emits the iframe container element every time it resizes.
  const element$ = widget$.pipe(
    switchMap(
      () =>
        new Observable<HTMLElement>((observer) => {
          const $iframeContainer = document.getElementById(
            'hubspot-messages-iframe-container',
          )

          if (!$iframeContainer) {
            observer.error(new Error('HubSpot iframe element not found'))
            return
          }

          const resizeObserver = new ResizeObserver(() => {
            observer.next($iframeContainer)
          })
          resizeObserver.observe($iframeContainer)

          return () => {
            resizeObserver.disconnect()
          }
        }),
    ),
    observeOn(animationFrameScheduler),
  )

  // Emits the open status of the widget.
  const isOpen$ = element$.pipe(
    map(($element) =>
      Boolean($element.querySelector('.hs-shadow-container.active')),
    ),
    mergeWith(
      widget$.pipe(
        switchMap((widget) =>
          fromEventPattern(
            (h) => widget.on('widgetClosed', h),
            (h) => widget.off('widgetClosed', h),
          ),
        ),
        map(() => false),
      ),
      requestOpen$.pipe(
        tap(() => window.HubSpotConversations?.widget.open()),
        map(() => true),
      ),
    ),
  )

  return element$.pipe(
    combineLatestWith(
      isOpen$.pipe(distinctUntilChanged()),
      isHidden$.pipe(distinctUntilChanged()),
      bottomOffset$.pipe(map(normalizeCssNumber), distinctUntilChanged()),
      isSpacious$.pipe(distinctUntilChanged()),
    ),
    observeOn(animationFrameScheduler),
    tap(([$element, isOpen, isHidden, bottomOffset, isSpacious]) => {
      $element.style.setProperty(
        'bottom',
        isOpen ? '0px' : bottomOffset,
        'important',
      )

      // Apply safe area padding to the iframe when open
      const $iframe = $element.querySelector<HTMLIFrameElement>(
        '#hubspot-conversations-iframe',
      )
      if ($iframe) {
        $iframe.style.setProperty(
          'padding-top',
          isOpen ? 'env(safe-area-inset-top)' : '0px',
          'important',
        )
        $iframe.style.setProperty(
          'padding-bottom',
          isOpen ? 'var(--app-safe-area-inset-bottom)' : '0px',
          'important',
        )
      }

      const shouldHide = isHidden || (!isSpacious && !isOpen)
      if (shouldHide) {
        $element.style.setProperty('display', 'none', 'important')
      } else {
        $element.style.setProperty('display', 'initial', 'important')
      }
    }),
  )
}
